/* eslint-disable react/self-closing-comp */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { useGetPostByIdQuery } from '@/redux/post/post.api.slice';
import PostCommentForm from '@/redux/commet/PostCommentForm';
import PostCommentList from '@/redux/commet/PostCommentList';
import LikeButton from '@/redux/like/LikeButton';
import PostDetailCarousel from '@/components/Post/PostDetailCarousel';
import NotFound from '../NotFound';
import ProfilePicture from '@/components/ui/profile-picture';
import ShareButton from '@/components/Post/ShareButton';
import DownloadPostAttachementsModal from '@/redux/post/DownloadPostAttachementsModal';
import DescriptionWithLinks from '@/components/Post/DescriptionWithLinks';
import OnefinityOfficialIcon from '@/assets/onefinity_officials_badge.png';
import { Card, CardTitle } from '@/components/ui/card';
import OnefinityDetailsCard from './OnefinityDetailsCard';
import ToolsDataCard from './ToolsDataCard';
import JobDetailsCard from './JobDetailsCard';
import JobSettingsCard from './JobSettingsCard';
import { Separator } from '@/components/ui/separator';
import { filterAttachmentsForCarousel } from '@/redux/post/post.utils';
import PostStatusBadge from '@/components/Post/PostStatusBadge';
import { IPostVisibility } from '@/redux/post/post';
import Loader from '@/components/common/Loader';
import SupportedMachines from './SupportedMachines';
import LicenceDetailsCard from './LicenseDetailsCard';
import OriginalMachineCard from './OriginalMachineCard';

function ModalDetail() {
  const { id: postId } = useParams();
  const { data: post, isLoading } = useGetPostByIdQuery(postId || '');
  const [modelIndex, setModelIndex] = useState<number>(0);

  if (isLoading) return <Loader title="Just a moment!" description="" />;

  if (!post || !postId || !post.attachments || !post.attachments.length) {
    return <NotFound />;
  }

  return (
    <div
      className="overflow-y-auto scroll-snap-type-y-mandatory pb-24"
      style={{ marginTop: 'var(--nav-height)' }}
    >
      {post?.visibility === 'private' && (
        <p className="w-full py-2 text-center text-sm text-muted-foreground font-secondary bg-[#FFEA99]">
          This project will not be visible to others until it is approved by
          filefinity.
        </p>
      )}
      <div className="mx-auto rounded-none border-none shadow-none px-5 py-[2rem]">
        <div className="flex flex-wrap gap-10 lg:gap-0 lg:flex-nowrap">
          <div className="flex flex-col lg:w-[50%] lg:border-r-[1px] border-[#E0E0E0] items-center justify-between px-0 lg:px-8 h-[670px] w-full border-r-0">
            <div className="flex justify-between w-full flex-wrap items-center">
              <div>
                <h1 className="font-primary font-bold text-2xl capitalize">
                  {post?.title}
                </h1>
                <Link to={`/${post?.user?.username}`}>
                  <div className="flex items-center gap-2 py-3">
                    <ProfilePicture
                      className="w-[30px] h-[30px]"
                      src={post?.user?.profilePicture}
                      alt={post?.user?.firstname}
                    />
                    <div className="flex gap-2 items-center">
                      <span className="text-sm text-[#666666]">
                        @{post?.user?.username}
                      </span>
                      {post.user.isOnefinityOfficial ? (
                        <img
                          src={OnefinityOfficialIcon}
                          className="h-6 w-6"
                          alt="onefinity official"
                        />
                      ) : (
                        // eslint-disable-next-line react/jsx-no-useless-fragment
                        <></>
                      )}
                    </div>
                  </div>
                </Link>
              </div>
              <PostStatusBadge
                visibility={post.visibility as IPostVisibility}
              />
            </div>
            <PostDetailCarousel
              attachments={filterAttachmentsForCarousel(post?.attachments)}
              modelIndex={modelIndex}
              setModelIndex={setModelIndex}
            />
          </div>
          <div className="h-[650px] overflow-y-auto lg:w-[50%] w-full custom-scrollbar relative">
            <div className="space-y-1 flex justify-between w-full items-center px-0 lg:px-[35px] sticky top-0 bg-white z-10 pb-5 shadow-sm mb-3">
              <h1 className="text-2xl font-semibold tracking-tight">
                Project Details
              </h1>
              <div className="flex gap-2">
                <DownloadPostAttachementsModal
                  attachments={post?.attachments}
                  postTitle={post.title}
                />
                <ShareButton
                  shareUrl={`${window.location.origin}/model/${postId}`}
                />
                <LikeButton
                  liked={post?.hasLikedByUser}
                  postId={postId}
                  likes={post?.likes}
                />
              </div>
            </div>
            <div className="px-0 lg:px-[35px]">
              <DescriptionWithLinks description={post?.description} />
              <Separator className="my-4" />
              <div>
                <ToolsDataCard
                  post={post}
                  modelIndex={modelIndex}
                  setModelIndex={setModelIndex}
                />
                <div className="grid gap-6 sm:grid-cols-2">
                  <JobDetailsCard post={post} />
                  <JobSettingsCard post={post} />
                </div>
                <OnefinityDetailsCard post={post} />
                <OriginalMachineCard post={post} />
                <SupportedMachines post={post} />
              </div>
              <LicenceDetailsCard post={post} />
              <Card className="my-5 p-6">
                <CardTitle># Hashtags</CardTitle>
                <div className="flex flex-wrap gap-5 mt-5">
                  {post.tags.map((tag) => (
                    <Badge
                      key={tag._id}
                      variant="secondary"
                      className="px-3 py-1 rounded-md font-medium capitalize"
                    >
                      #{tag.name}
                    </Badge>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-[#F7F9FA] px-8 lg:px-20 py-8 scroll-snap-align-start">
        <h3 className="font-secondary text-xl">Comments ({post?.comments})</h3>
      </div>
      <div className="mt-8 py-[2rem] mx-auto px-4 lg:px-14 flex justify-between gap-[40px] lg:flex-nowrap flex-wrap">
        <PostCommentList postId={postId} />
        <PostCommentForm postId={postId} />
      </div>
    </div>
  );
}

export default ModalDetail;
